import { useState } from 'react';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  NumberInput,
  TextField,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
} from 'react-admin';
import {
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';
import { updateTokenConfig } from '../../../services/xpxPerpsChainTokenConfigService';
import './init';

const PERMISSION_RESOURCE = 'xpx-perps:token-config';

type TogglePending = {
  record: any;
  field: 'depositEnabled' | 'withdrawalEnabled';
  nextValue: boolean;
};

const truncateAddress = (addr: string) =>
  addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '-';

const ListFilter = (props: any) => (
  <Filter {...props}>
    <NumberInput source="chainId" label="module.xpxPerpsTokenConfigs.chainId" alwaysOn />
  </Filter>
);

const XpxPerpsTokenConfigsList = () => {
  const t = useTranslate();
  const refresh = useRefresh();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionData>();
  const canWrite = hasPermission(permissions?.permissions, PERMISSION_RESOURCE, 'write');
  const [pending, setPending] = useState<TogglePending | null>(null);
  const [loading, setLoading] = useState(false);

  const handleToggleClick = (
    record: any,
    field: 'depositEnabled' | 'withdrawalEnabled',
    nextValue: boolean,
  ) => {
    setPending({ record, field, nextValue });
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setLoading(true);
    try {
      const { record, field, nextValue } = pending;
      await updateTokenConfig(
        Number(record.chainId),
        record.tokenAddress,
        field === 'depositEnabled' ? nextValue : record.depositEnabled,
        field === 'withdrawalEnabled' ? nextValue : record.withdrawalEnabled,
      );
      refresh();
    } finally {
      setLoading(false);
      setPending(null);
    }
  };

  const handleCancel = () => setPending(null);

  const action = pending?.nextValue
    ? t('module.xpxPerpsTokenConfigs.confirmEnable')
    : t('module.xpxPerpsTokenConfigs.confirmDisable');
  const field = pending ? t(`module.xpxPerpsTokenConfigs.${pending.field}`) : '';

  return (
    <DashboardContent>
      <PermissionGuard resource={PERMISSION_RESOURCE} action="read" fallback={<ErrorPage403 />}>
        <CustomBreadcrumbs
          heading={t('module.xpxPerpsTokenConfigs.title')}
          links={[
            { name: t('resource.perpsCore'), href: '#' },
            { name: t('module.xpxPerpsTokenConfigs.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<ListFilter />}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid rowClick={false} bulkActionButtons={false} empty={<EmptyContent />}>
                <FunctionField
                  label="module.xpxPerpsTokenConfigs.symbol"
                  sortable={false}
                  render={(record: any) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar src={record.logo} alt={record.symbol} sx={{ width: 24, height: 24 }} />
                      <Typography variant="body2">{record.symbol}</Typography>
                    </Stack>
                  )}
                />
                <TextField
                  source="chainId"
                  label="module.xpxPerpsTokenConfigs.chainId"
                  sortable={false}
                />
                <FunctionField
                  label="module.xpxPerpsTokenConfigs.tokenAddress"
                  sortable={false}
                  render={(record: any) => (
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                      title={record.tokenAddress}
                      onClick={() => {
                        navigator.clipboard.writeText(record.tokenAddress);
                        notify('Copied to clipboard', { type: 'info' });
                      }}
                    >
                      {truncateAddress(record.tokenAddress)}
                    </Typography>
                  )}
                />
                <TextField
                  source="decimals"
                  label="module.xpxPerpsTokenConfigs.decimals"
                  sortable={false}
                />
                <TextField
                  source="collectThreshold"
                  label="module.xpxPerpsTokenConfigs.collectThreshold"
                  sortable={false}
                />
                <FunctionField
                  label="module.xpxPerpsTokenConfigs.depositEnabled"
                  sortable={false}
                  render={(record: any) => (
                    <Switch
                      checked={record.depositEnabled}
                      size="small"
                      disabled={!canWrite}
                      onChange={(e) =>
                        handleToggleClick(record, 'depositEnabled', e.target.checked)
                      }
                    />
                  )}
                />
                <FunctionField
                  label="module.xpxPerpsTokenConfigs.withdrawalEnabled"
                  sortable={false}
                  render={(record: any) => (
                    <Switch
                      checked={record.withdrawalEnabled}
                      size="small"
                      disabled={!canWrite}
                      onChange={(e) =>
                        handleToggleClick(record, 'withdrawalEnabled', e.target.checked)
                      }
                    />
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={Boolean(pending)} onClose={handleCancel} maxWidth="xs" fullWidth>
          <DialogTitle>{t('module.xpxPerpsTokenConfigs.confirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {t('module.xpxPerpsTokenConfigs.confirmMessage', {
                action,
                field,
                symbol: pending?.record?.symbol ?? '',
                chainId: pending?.record?.chainId ?? '',
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} disabled={loading}>
              {t('ra.action.cancel')}
            </Button>
            <Button onClick={handleConfirm} variant="contained" disabled={loading || !canWrite}>
              {t('ra.action.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XpxPerpsTokenConfigsList;
